import { API_URL } from '@/lib/constants'

const SECRET_STORAGE_KEY = 'admin-api-secret'
const EXPIRES_STORAGE_KEY = 'admin-auth-expires'
const SESSION_TTL_MS = 24 * 60 * 60 * 1000
const authListeners = new Set<() => void>()

function notifyAdminAuthChanged(): void {
  for (const listener of authListeners)
    listener()
}

export function subscribeAdminAuth(listener: () => void): () => void {
  authListeners.add(listener)
  return () => authListeners.delete(listener)
}

export function isAdminAuthStorageKey(key: string | null): boolean {
  return key === SECRET_STORAGE_KEY || key === EXPIRES_STORAGE_KEY || key === null
}

export function getAdminExpiry(): number | null {
  const expires = Number(localStorage.getItem(EXPIRES_STORAGE_KEY))
  return Number.isFinite(expires) && expires > 0 ? expires : null
}

export function getAdminSecret(): string | null {
  const secret = localStorage.getItem(SECRET_STORAGE_KEY)
  if (!secret)
    return null

  const expires = getAdminExpiry()
  if (expires === null || Date.now() >= expires) {
    clearAdminSecret()
    return null
  }

  return secret
}

export function setAdminSecret(secret: string): void {
  localStorage.setItem(SECRET_STORAGE_KEY, secret)
  localStorage.setItem(EXPIRES_STORAGE_KEY, String(Date.now() + SESSION_TTL_MS))
  notifyAdminAuthChanged()
}

export function clearAdminSecret(): void {
  const hadSession = localStorage.getItem(SECRET_STORAGE_KEY) !== null
    || localStorage.getItem(EXPIRES_STORAGE_KEY) !== null
  localStorage.removeItem(SECRET_STORAGE_KEY)
  localStorage.removeItem(EXPIRES_STORAGE_KEY)
  if (hadSession)
    notifyAdminAuthChanged()
}

export function adminAuthHeaders(): Record<string, string> {
  const secret = getAdminSecret()
  return secret ? { authorization: `Bearer ${secret}` } : {}
}

export async function fetchWithAdminAuth(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const headers = init?.headers ?? (input instanceof Request ? input.headers : undefined)
  const authorization = new Headers(headers).get('authorization')
  const requestSecret = authorization?.startsWith('Bearer ')
    ? authorization.slice('Bearer '.length)
    : null
  const response = await fetch(input, init)
  if ((response.status === 401 || response.status === 403)
    && requestSecret !== null
    && getAdminSecret() === requestSecret) {
    clearAdminSecret()
  }
  return response
}

export async function verifyAdminSecret(
  secret: string,
): Promise<
  | { ok: true, email: string }
  | { ok: false, error: string, rejected: boolean }
> {
  try {
    const res = await fetch(`${API_URL}/api/queryRouter.adminWhoami`, {
      headers: { authorization: `Bearer ${secret}` },
    })
    if (!res.ok) {
      if (res.status === 401)
        return { ok: false, error: 'Invalid API secret.', rejected: true }
      if (res.status === 403)
        return { ok: false, error: 'This account does not have admin access.', rejected: true }
      return { ok: false, error: `Verification failed (HTTP ${res.status}).`, rejected: false }
    }
    const body = await res.json() as {
      result?: { data?: { json?: { email?: string } } }
    }
    const email = body.result?.data?.json?.email
    if (!email)
      return { ok: false, error: 'Verification failed (invalid response).', rejected: false }
    return { ok: true, email }
  }
  catch {
    return {
      ok: false,
      error: 'Could not reach the API. Check your connection.',
      rejected: false,
    }
  }
}
